import { getCookie, setSecureCookie } from '../utils/secureCookies';

import type { AuthParams } from './auth';
import { firebaseAuth } from './firebase';

const events = [
  /*
   Fires once a user has successfully registered and reached the OTP part.
   */
  'sign_up_click', // ✔️

  /*
   Fires once a user has successfully filled/verified the OTP authentication.
   */
  'sign_up_verified', // ✔️

  /*
   Everytime a user is purchasing a certain option we need to fire the purchase datalayer.
   Pay attention the numbers are dynamic.
   'value': 12345, // the price paid.
   'currency': 'USD',
   'transaction_id':'abc_1234', // the transaction id for this particular purchase
   'user_id': '12345789' // the user id that made the purchase
   */
  'purchase_event', // ✔️

  /*
   Firing only for the first time deposit  pay attention the numbers are dynamic.
   'value': 12345, // the price paid.
   'currency': 'USD',
   'transaction_id':'abc_1234', // the transaction id for this particular purchase
   'user_id': '12345789' // the user id that made the purchase
   */
  'ftd_event', // ✔️

  /*
  Firing once on the page load
  Should populate one of those:
      - organic - the “document.referrer” is www.google.com and we don't have “UTM_” parameters in the URL
      - direct - the “document.referrer” is empty and we don't have “UTM_” Parameters in the URL
      - UTM Source contain logic:
          - search - will contain a utm_source=search
          - meta - will contain a utm_source=meta
          - tt - will contain a utm_source=tt
          - yt - will contain a utm_source=yt
          - display - will contain a utm_source=display
          - affiliates_seo - will contain a utm_source=affiliates_seo
          - affiliates_search -will contain a utm_source=affiliates_search
          - email_marketing - will contain a utm_source=email_marketing
          - sms - will contain a utm_source=sms
          - native_content - will contain a utm_source=native_content
          - social_media - will contain a utm_source=social_media
   'source': ⬆️
   */
  'visit_source', // ✔️

  /*
   Firing once a user has successfully verified his phone number.
   */
  'phone_verified', // ✔️

  /*
   Firing once a user completed purchase and he is a valuable customer.
   */
  'pvp_purchase', // ✔️
] as const;

const UTM_TERM_COOKIE_NAME = 'utm_term';
const AFFILIATE_COOKIE_NAME = 'affiliates_cookie';

// Affiliates UTM sources: seo, search, social
const AFFILIATES_SEO = 'affiliates_seo';
const AFFILIATES_SEARCH = 'affiliates_search';
const AFFILIATES_SOCIAL = 'affiliates_social';

// Partnerships UTM sources: seo, search, offerwall
const PARTNERS_SEO = 'partnerships_seo';
const PARTNERS_SEARCH = 'partnerships_search';
const PARTNERS_OFFER_WALL = 'partnerships_offerwall';

// Media Force affiliate
const MEDIA_FORCE_TRANSACTION_ID_COOKIE_NAME = 'media_force_transaction_id';

export const utmSourceSet = new Set([
  // UTM sources for visit_source event
  'search',
  'meta',
  'tt',
  'yt',
  'display',
  'email_marketing',
  'sms',
  'native_content',
  'social_media',
  'partnerships_native',
  // UTM sources for visit_source event and affiliates cookie logic
  AFFILIATES_SOCIAL,
  AFFILIATES_SEO,
  AFFILIATES_SEARCH,
  PARTNERS_SEO,
  PARTNERS_SEARCH,
  PARTNERS_OFFER_WALL,
]);

export type Params = Record<string, string | number | boolean | undefined>;

export function googleTagEvent(eventName: (typeof events)[number], params?: Params): void {
  if (typeof window === 'undefined' || !window?.dataLayer) {
    return;
  }
  window?.dataLayer.push({
    event: eventName,
    ...params,
  });
}

// Urchin Tracking Module (UTM) parameters
function resolveUTMParams(): Record<string, string> {
  const searchParams = new URLSearchParams(window.location.search);
  const map: Record<string, string> = {};
  for (const [key, value] of searchParams.entries()) {
    map[key.toLowerCase()] = value;
  }
  return map;
}

export function sendGoogleTagUTMSource() {
  const { referrer } = document;
  const params = resolveUTMParams();

  let source: string = 'direct';
  const utmSource = params['utm_source'];

  if (utmSource && utmSourceSet.has(utmSource)) {
    source = utmSource;
  } else if (referrer.indexOf('www.google.com') !== -1) {
    source = 'organic';
  }

  if (source.toLowerCase() === 'direct') {
    return;
  }

  googleTagEvent('visit_source', { source });
}

function addAffiliateData() {
  const data: Record<string, unknown> = {};
  const affiliate = getCookie(AFFILIATE_COOKIE_NAME);
  if (affiliate) {
    data['affiliate_data'] = affiliate;
  }
  const utmTerm = getCookie(UTM_TERM_COOKIE_NAME);
  if (utmTerm) {
    data['utm_term'] = utmTerm;
  }
  return data;
}

export function sendGoogleTagSuccessPayment(params: Params) {
  const { isPvpPurchase, ...rest } = params;
  const eventParams: Params = {
    user_id: firebaseAuth.currentUser?.uid,
    currency: 'USD',
    ...rest,
    ...addAffiliateData(),
  };

  googleTagEvent('purchase_event', eventParams);
  if (isPvpPurchase) {
    googleTagEvent('pvp_purchase', eventParams);
  }
}

export function sendGoogleTagFtdEvent(params: Params) {
  const eventParams: Params = {
    user_id: firebaseAuth.currentUser?.uid,
    currency: 'USD',
    ...params,
    ...addAffiliateData(),
  };

  googleTagEvent('ftd_event', eventParams);
}

export function sendGoogleTagSignUpClick(params: { provider: AuthParams['provider']; email?: string }) {
  const eventParams: Params = {
    ...params,
    ...addAffiliateData(),
  };

  googleTagEvent('sign_up_click', eventParams);
}

const AFFILIATES = new Set([
  AFFILIATES_SEO,
  AFFILIATES_SEARCH,
  AFFILIATES_SOCIAL,
  PARTNERS_OFFER_WALL,
  PARTNERS_SEO,
  PARTNERS_SEARCH,
]);
export function handleAffiliateUTM() {
  const params = resolveUTMParams();
  const utmSource = params['utm_source'];
  const utmCampaign = params['utm_campaign'];

  if (AFFILIATES.has(utmSource) && utmCampaign) {
    const value = utmCampaign + '-' + utmSource;
    const saved = getCookie(AFFILIATE_COOKIE_NAME);
    if (saved === value) {
      return;
    }
    // Set secure affiliate cookie with 7 day expiry
    setSecureCookie(AFFILIATE_COOKIE_NAME, value, { expires: 7 });
  }

  if (params['utm_term']) {
    // Set secure UTM term cookie with 7 day expiry
    setSecureCookie(UTM_TERM_COOKIE_NAME, params['utm_term'], { expires: 7 });
  }

  // If the user came from a media force affiliate, save the transaction id
  if (utmSource === 'native' && utmCampaign === 'media_force' && params['utm_medium'] === '1') {
    // Set secure media force transaction ID cookie with 7 day expiry
    setSecureCookie(MEDIA_FORCE_TRANSACTION_ID_COOKIE_NAME, params['utm_term'], { expires: 7 });
  }
}

export function getMediaForceTransactionId() {
  return getCookie(MEDIA_FORCE_TRANSACTION_ID_COOKIE_NAME);
}
